import React, { useState } from 'react';
import { Mail, RefreshCw, LogOut, CheckCircle2, AlertCircle } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface EmailVerificationBlockingProps {
  user: any;
  onVerified: () => void;
}

export function EmailVerificationBlocking({ user, onVerified }: EmailVerificationBlockingProps) {
  const [isReloading, setIsReloading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleReload = async () => {
    setIsReloading(true);
    setError(null);
    try {
      await auth.currentUser?.reload();
      if (auth.currentUser?.emailVerified) {
        onVerified();
      } else {
        setError('El correo aún no ha sido verificado. Por favor revisa tu bandeja de entrada.');
      }
    } catch (err) {
      console.error('Error al recargar usuario:', err);
      setError('Error al comprobar el estado. Inténtalo de nuevo.');
    } finally {
      setIsReloading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setResendStatus('idle');
    setError(null);
    try {
      if (auth.currentUser) {
        const functionsInstance = getFunctions();
        const resendVerification = httpsCallable(functionsInstance, 'resendVerificationEmail');
        await resendVerification();
        setResendStatus('success');
        // Reset success message after 5 seconds
        setTimeout(() => setResendStatus('idle'), 5000);
      }
    } catch (err: any) {
      console.error('Error al reenviar correo:', err);
      setResendStatus('error');
      if (err.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Por favor espera un momento antes de intentarlo de nuevo.');
      } else {
        setError('No se pudo enviar el correo. Inténtalo de nuevo.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 bg-black/20 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-[#0f172a]/50 backdrop-blur-xl rounded-[2rem] border border-yellow-400/30 w-full max-w-md shadow-[0_0_50px_rgba(250,204,21,0.2)] relative overflow-hidden p-6 text-center animate-in zoom-in-95 duration-200">
        {/* Yellow glow effect */}
        <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-yellow-400/20 blur-[80px] opacity-20 pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-yellow-400/10 blur-[80px] opacity-10 pointer-events-none" />


        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-lime-400/10 border border-lime-400/30 flex items-center justify-center mb-6">
          <Mail className="w-8 h-8 text-lime-400" />
        </div>

        {/* Content */}
        <h2 className="text-2xl font-black text-white mb-2 theme-font-secondary uppercase tracking-wider">
          Te enviamos un correo
        </h2>
        <p className="text-sm text-white/70 mb-2 leading-relaxed">
          Verifica tu correo y da click en el link de validación.
        </p>
        <p className="text-xs text-white/50 mb-4 leading-relaxed">
          Enviado a <span className="text-yellow-400 font-bold">{user?.email}</span>
        </p>

        {/* Tip */}
        <div className="text-[11px] text-white/30 mb-6 flex items-center justify-center gap-1">
          <span>Tip: Revisa la carpeta de spam si aún no ha llegado.</span>
        </div>

        {/* Feedback Messages */}
        {resendStatus === 'success' && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2 text-green-400 text-xs justify-center">
            <CheckCircle2 size={14} />
            <span>¡Correo enviado con éxito!</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-red-400 text-xs justify-center">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleReload}
            disabled={isReloading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-yellow-400 text-black font-extrabold text-sm uppercase tracking-wider rounded-xl shadow-[0_4px_15px_rgba(250,204,21,0.3)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100"
          >
            {isReloading ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <CheckCircle2 size={18} />
            )}
            <span>Ya verifiqué</span>
          </button>

          <button
            onClick={handleResend}
            disabled={isResending || resendStatus === 'success'}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-white/5 text-white font-bold text-sm uppercase tracking-wider rounded-xl border border-white/10 hover:bg-white/10 active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100"
          >
            {isResending ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <Mail size={18} />
            )}
            <span>Reenviar correo</span>
          </button>

          <div className="pt-2">
            <button
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 mx-auto text-white/50 hover:text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <LogOut size={14} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
